export interface utteranceData {

  character: string;
  word: string[];
  elapsedTime: number;
  charIndex: number;
  speaking: boolean;
}
export interface TTSOptions {
  voice?: string;
  rate?: number;
  pitch?: number;
  volume?: number;
}

export class TTSService {
  private synthesis: SpeechSynthesis;
  private currentUtterance: SpeechSynthesisUtterance | null = null;
  private defaultVoice: string;
  private boundaryFired: boolean = false;
  private fallbackTimers: ReturnType<typeof setTimeout>[] = [];

  constructor() {
    this.synthesis = window.speechSynthesis;
    this.defaultVoice = import.meta.env.VITE_TTS_VOICE || 'default';
  }

  async speak(text: string, options: TTSOptions = {}, onSpeakChar: { (data: utteranceData): void }): Promise<void> {
    return new Promise((resolve, reject) => {
      // Cancel any ongoing speech
      this.synthesis.cancel();
      this.clearFallbackTimers();
      this.boundaryFired = false;

      const utterance = new SpeechSynthesisUtterance(text);
      this.currentUtterance = utterance;

      // Set voice if specified
      if (options.voice) {

        const voice = this.synthesis.getVoices().find(v => v.name === options.voice);
        if (voice) {
          utterance.lang = "es-MX";
          utterance.voice = voice;
        }
      } else if (this.defaultVoice !== 'default') {
        const voice = this.synthesis.getVoices().find(v => v.name === this.defaultVoice);
        if (voice) {
          utterance.voice = voice;
        }
      }

      // Set other parameters
      utterance.rate = options.rate ?? 1.0;
      utterance.pitch = options.pitch ?? 1.0;
      utterance.volume = options.volume ?? 1.0;

      utterance.onboundary = (event) => {
        if (event.name === 'word') {
          this.boundaryFired = true;
          onSpeakChar({
            charIndex: event.charIndex,
            elapsedTime: event.elapsedTime,
            character: text.charAt(event.charIndex),
            word: text.substring(event.charIndex).split(' '),
            speaking: true // Get the full word
          });
        }
      };

      // Fallback for engines that don't support onboundary
      utterance.onstart = () => {
        // Wait 200ms to see if native boundaries fire
        const checkTimer = setTimeout(() => {
          if (this.synthesis.speaking && !this.boundaryFired) {
            console.warn("TTS: onboundary not supported by this voice. Starting manual simulation.");
            this.startManualBoundarySimulation(text, utterance.rate, onSpeakChar);
          }
        }, 200);
        this.fallbackTimers.push(checkTimer);
      };

      utterance.onend = () => {
        this.currentUtterance = null;
        this.clearFallbackTimers();
        onSpeakChar({
          charIndex: 0,
          elapsedTime: 0,
          character: 'SIL',
          word: [],
          speaking: false // Get the full word
        });
        resolve();
      };

      utterance.onerror = (event) => {
        this.currentUtterance = null;
        this.clearFallbackTimers();
        reject(new Error(`TTS error: ${event.error}`));
      };

      this.synthesis.speak(utterance);
    });
  }

  stop(): void {
    this.synthesis.cancel();
    this.currentUtterance = null;
    this.clearFallbackTimers();
  }

  pause(): void {
    this.synthesis.pause();
  }

  resume(): void {
    this.synthesis.resume();
  }

  isSpeaking(): boolean {
    return this.synthesis.speaking;
  }

  isPaused(): boolean {
    return this.synthesis.paused;
  }

  private clearFallbackTimers(): void {
    this.fallbackTimers.forEach(id => clearTimeout(id));
    this.fallbackTimers = [];
  }

  /**
   * Simulates word boundaries based on character length and estimated CPS
   */
  private startManualBoundarySimulation(
    text: string,
    rate: number,
    onSpeakChar: (data: utteranceData) => void
  ): void {
    // ~15 chars per second is a good baseline for rate 1.0
    const estimatedCPS = 15 * rate;

    // Split by whitespace but keep track of indices
    const words = text.split(/(\s+)/);
    let currentPos = 0;

    words.forEach((part) => {
      if (part.trim().length > 0) {
        const charIndex = currentPos;
        const delay = (charIndex / estimatedCPS) * 1000;

        const timer = setTimeout(() => {
          if (this.synthesis.speaking && !this.boundaryFired) {
            onSpeakChar({
              charIndex,
              elapsedTime: delay,
              character: text.charAt(charIndex),
              word: text.substring(charIndex).split(' '),
              speaking: true
            });
          }
        }, delay);
        this.fallbackTimers.push(timer);
      }
      currentPos += part.length;
    });
  }

  getVoices(): SpeechSynthesisVoice[] {
    return this.synthesis.getVoices();
  }


  getCurrentUtterance(): SpeechSynthesisUtterance | null {
    return this.currentUtterance;
  }

  async generatePhonemes(text: string): Promise<Array<{ phoneme: string, start: number, end: number }>> {
    // Simple phoneme generation based on text
    // This is a basic implementation - can be enhanced with a proper phonemizer
    if (!text) {
      return [];
    }
    const words = text.split(' ');
    const phonemes: Array<{ phoneme: string, start: number, end: number }> = [];
    let currentTime = 0;
    const avgWordDuration = 0.3; // seconds per word

    words.forEach((word) => {
      console.log(word)
      const wordPhonemes = this.wordToPhonemes(word);
      const wordDuration = wordPhonemes.length * avgWordDuration / wordPhonemes.length;

      wordPhonemes.forEach((phoneme, pIndex) => {
        phonemes.push({
          phoneme,
          start: currentTime + (pIndex * wordDuration / wordPhonemes.length),
          end: currentTime + ((pIndex + 1) * wordDuration / wordPhonemes.length)
        });
      });

      currentTime += wordDuration;
    });
    return phonemes;
  }

  private wordToPhonemes(word: string): string[] {
    // Very basic phoneme approximation
    // In a real implementation, use a proper phonemization library
    const simplePhonemes: string[] = [];
    for (let i = 0; i < word.length; i++) {
      const char = word[i].toLowerCase();
      if ("aeiou".includes(char)) simplePhonemes.push(char.toUpperCase());
      if ("mnpbd".includes(char)) simplePhonemes.push("M");
      if ("f".includes(char)) simplePhonemes.push("F");
      simplePhonemes.push("E"); // aproximación genérica
    }
    return simplePhonemes.length > 0 ? simplePhonemes : ['SIL'];
  }
}